<script lang="ts" module>
  import { formatNumber } from '../../utils/format';
</script>

<script lang="ts">
  import type { Participant } from '@pin/schemas/admin';
  import Icon from '../../lib/Icon.svelte';
  import { maskIcon } from '../../lib/icons/maskIcon';

  interface Props {
    participant: Participant;
    guessCount?: number;
    favoriteCount?: number;
  }

  const { participant, guessCount, favoriteCount }: Props = $props();
</script>

<div class="mask-card">
  <div class="info">
    <div class="mask-card-text">
      <span class="chip" class:unmasked={participant.unmaskedAt != null}>
        {#if participant.unmaskedAt == null}
          <Icon svg={maskIcon} />
        {/if}
        {participant.unmaskedAt != null ? 'Avmaskert' : 'Maskert'}
      </span>
    </div>
    <dl class="stats">
      <div class="figure">
        <dt>Gjett</dt>
        <dd>{guessCount == null ? '–' : formatNumber(guessCount)}</dd>
      </div>
      <div class="figure">
        <dt>Favoritter</dt>
        <dd>
          {favoriteCount == null ? '–' : formatNumber(favoriteCount)}
        </dd>
      </div>
    </dl>
  </div>
  {#if participant.imageRef}
    <img class="portrait" src={participant.imageRef.src} alt="" />
  {:else}
    <span class="portrait placeholder" aria-hidden="true">
      {participant.maskName.charAt(0).toUpperCase()}
    </span>
  {/if}
</div>

<style>
  .mask-card {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border: var(--border-width) solid var(--border);
    border-radius: var(--radius-lg);
    background: var(--surface-raised);
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
  }

  .portrait {
    inline-size: 40%;
    max-inline-size: 10rem;
    border-start-end-radius: var(--radius-lg);
    border-end-end-radius: var(--radius-lg);
    object-fit: contain;
    align-self: flex-end;
  }

  .placeholder {
    display: grid;
    place-items: center;
    color: var(--text-muted);
    font-size: var(--text-xl);
    font-weight: var(--nrk-sans-weight-bold);
  }

  .mask-card-text {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-pill);
    font-size: var(--text-sm);
    font-weight: var(--nrk-sans-weight-medium);
    white-space: nowrap;
    background: var(--success-subtle);
    color: var(--success);

    &.unmasked {
      background: var(--surface-sunken);
      color: var(--text-muted);
    }
  }

  .stats {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin: 0;
  }

  .figure {
    flex: 1 1 5rem;
    padding: var(--space-2) var(--space-3);
    border: var(--border-width) solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface-base);

    & dt {
      color: var(--text-muted);
      font-size: var(--text-xs);
    }

    & dd {
      margin: 0;
      font-size: var(--text-lg);
      font-weight: var(--nrk-sans-weight-medium);
      font-variant-numeric: tabular-nums;
    }
  }
</style>
